import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import GitHubIcon from "@mui/icons-material/GitHub";
import Header from "../../layouts/Header/header";
import Footer from "../../layouts/Footer/footer";
import { useThemeContext } from "../../context/ThemeContext";
import useSoundFx from "../../hooks/useSoundFx";
import projects from "../../data/projects";
import Reveal from "../../components/Reveal";

/* ─── Reusable section card ─── */
const Section = ({ title, children, isDark, id }) => {
  const sectionBg = isDark
    ? "rgba(255,255,255,0.06)"
    : "rgba(255,255,255,0.6)";
  const sectionBorder = isDark
    ? "1px solid rgba(255,255,255,0.1)"
    : "1px solid rgba(0,0,0,0.07)";

  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{
        mb: 3,
        p: { xs: 2.5, md: 4 },
        borderRadius: "16px",
        background: sectionBg,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: sectionBorder,
        transition: "background 0.3s ease, border-color 0.3s ease",
      }}
    >
      <Typography
        id={id}
        component="h2"
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: 2.5,
          textTransform: "uppercase",
          color: "#00e676",
          mb: 2,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
};

/* ─── Sub-heading inside Spotlight section ─── */
const SubHeading = ({ children, isDark }) => (
  <Typography
    fontWeight={600}
    sx={{
      mb: 1,
      fontFamily: "'DM Sans', sans-serif",
      fontSize: "1rem",
      color: isDark ? "rgba(255,255,255,0.9)" : "#0d1b2a",
    }}
  >
    {children}
  </Typography>
);

/* ─── Body text ─── */
const BodyText = ({ children, sx = {} }) => (
  <Typography
    sx={{
      lineHeight: 1.85,
      fontSize: { xs: "0.95rem", md: "1rem" },
      opacity: 0.82,
      fontFamily: "'DM Sans', sans-serif",
      ...sx,
    }}
  >
    {children}
  </Typography>
);

/* ═══════════════════════════════════════════════ */
const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDark } = useThemeContext();
  const { playClick, playHover } = useSoundFx();

  const project = projects.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    navigate("/");
    return null;
  }

  /* ── theme-aware tokens ── */
  const textPrimary = isDark ? "white" : "#0d1b2a";
  const overlayBg = isDark ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.2)";
  const chipBg = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
  const chipBorder = isDark
    ? "1px solid rgba(255,255,255,0.2)"
    : "1px solid rgba(0,0,0,0.1)";
  const chipColor = isDark ? "white" : "#0d1b2a";
  const backColor = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.4)";

  const handleBack = () => {
    playClick();
    navigate(-1);
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: textPrimary,
        fontFamily: "'DM Sans', sans-serif",
        transition: "color 0.3s, background-color 0.3s",
      }}
    >
      {/* Fixed overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: overlayBg,
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      <Header />

      <Box
        component="main"
        id="main-content"
        sx={{
          position: "relative",
          zIndex: 2,
          pt: { xs: "80px", md: "96px" },
          pb: 12,
        }}
      >
        <Container maxWidth="md">
          {/* ─── Back button (top) ─── */}
          <Box
            role="button"
            tabIndex={0}
            aria-label="Back to Portfolio"
            onClick={handleBack}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBack();
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              color: backColor,
              fontSize: "0.9rem",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500,
              mt: 3,
              mb: 5,
              transition: "color 0.2s",
              "&:hover": { color: "#00e676" },
              userSelect: "none",
            }}
          >
            <ArrowBackIcon fontSize="small" />
            Back to Portfolio
          </Box>

          {/* ─── Hero ─── */}
          <Box sx={{ mb: 6 }}>
            <Reveal>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  letterSpacing: 2.5,
                  opacity: 0.45,
                  textTransform: "uppercase",
                  fontFamily: "'DM Sans', sans-serif",
                  mb: 1.5,
                }}
              >
                {project.year}
              </Typography>
            </Reveal>

            <Reveal delay={80}>
              <Typography
                variant="h3"
                fontWeight={600}
                sx={{
                  lineHeight: 1.2,
                  mb: 1.5,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: { xs: "1.7rem", md: "2.4rem" },
                }}
              >
                {project.title}
              </Typography>
            </Reveal>

            <Reveal delay={160}>
              <Typography
                sx={{
                  opacity: 0.55,
                  mb: 3,
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                {project.role}
              </Typography>
            </Reveal>

            {/* Tech chips — cascade */}
            <Reveal delay={240}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3.5 }}>
                {project.tech.map((t, i) => (
                  <Box
                    key={t}
                    onMouseEnter={playHover}
                    sx={{
                      backgroundColor: chipBg,
                      color: chipColor,
                      px: 1.5,
                      py: 0.4,
                      borderRadius: "999px",
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      backdropFilter: "blur(4px)",
                      border: chipBorder,
                    }}
                  >
                    {t}
                  </Box>
                ))}
              </Box>
            </Reveal>

            {/* External links */}
            {(project.liveUrl || project.githubUrl) && (
              <Reveal delay={320}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {project.liveUrl && (
                    <Box
                      component="a"
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={playClick}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: "#00e676",
                        fontSize: "0.9rem",
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        textDecoration: "none",
                        border: "1px solid rgba(0,230,118,0.35)",
                        px: 2.5,
                        py: 0.9,
                        borderRadius: "999px",
                        backgroundColor: "rgba(0,230,118,0.08)",
                        transition: "background 0.2s",
                        "&:hover": { backgroundColor: "rgba(0,230,118,0.18)" },
                      }}
                    >
                      <OpenInNewIcon sx={{ fontSize: "1rem" }} />
                      Live Demo
                    </Box>
                  )}
                  {project.githubUrl && (
                    <Box
                      component="a"
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={playClick}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                        fontSize: "0.9rem",
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        textDecoration: "none",
                        border: isDark
                          ? "1px solid rgba(255,255,255,0.2)"
                          : "1px solid rgba(0,0,0,0.15)",
                        px: 2.5,
                        py: 0.9,
                        borderRadius: "999px",
                        transition: "all 0.2s",
                        "&:hover": { color: textPrimary },
                      }}
                    >
                      <GitHubIcon sx={{ fontSize: "1rem" }} />
                      GitHub
                    </Box>
                  )}
                </Box>
              </Reveal>
            )}
          </Box>

          {/* ─── Screenshot gallery (only if images exist) ─── */}
          {project.images && project.images.length > 0 && (
            <Box sx={{ mb: 3, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {project.images.map((src, i) => (
                <Reveal key={i} variant="fadeScale" delay={i * 120}>
                  <Box
                    component="img"
                    src={src}
                    alt={`${project.title} screenshot ${i + 1}`}
                    sx={{
                      width: "100%",
                      borderRadius: "12px",
                      objectFit: "cover",
                      border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
                      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.4)" : "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                  />
                </Reveal>
              ))}
            </Box>
          )}

          {/* ─── Introduction ─── */}
          <Reveal>
            <Section title="Introduction" id="sec-intro" isDark={isDark}>
              <BodyText>{project.introduction}</BodyText>
            </Section>
          </Reveal>

          {/* ─── Purpose & Goal ─── */}
          <Reveal delay={60}>
            <Section title="Purpose & Goal" id="sec-purpose" isDark={isDark}>
              <BodyText>{project.purposeAndGoal}</BodyText>
            </Section>
          </Reveal>

          {/* ─── Spotlight ─── */}
          <Reveal delay={120}>
            <Section title="Spotlight" id="sec-spotlight" isDark={isDark}>
              <SubHeading isDark={isDark}>Killer Feature</SubHeading>
              <BodyText sx={{ mb: 3.5 }}>{project.spotlight.feature}</BodyText>

              <SubHeading isDark={isDark}>Technical Challenges</SubHeading>
              <BodyText sx={{ mb: 3.5 }}>{project.spotlight.challenges}</BodyText>

              <SubHeading isDark={isDark}>How It Was Solved</SubHeading>
              <BodyText>{project.spotlight.solution}</BodyText>
            </Section>
          </Reveal>

          {/* ─── Current Status (optional) ─── */}
          {project.currentStatus && (
            <Reveal delay={60}>
              <Section title="Current Status" id="sec-status" isDark={isDark}>
                <BodyText>{project.currentStatus}</BodyText>
              </Section>
            </Reveal>
          )}

          {/* ─── Lessons Learned ─── */}
          <Reveal delay={60}>
            <Section title="Lessons Learned" id="sec-lessons" isDark={isDark}>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {project.lessonsLearned.map((lesson, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      lineHeight: 1.85,
                      opacity: 0.82,
                      fontFamily: "'DM Sans', sans-serif",
                      mb: i < project.lessonsLearned.length - 1 ? 1.5 : 0,
                      "& ::marker": { color: "#00e676" },
                    }}
                  >
                    {lesson}
                  </Box>
                ))}
              </Box>
            </Section>
          </Reveal>

        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default ProjectDetail;
